import * as React from 'react';
import { useMemo } from 'react';
import { AgentSkills, KeyValue, SkillType } from '../../../../../types/AiAgentV2';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from 'sbx-crm-menu-ui';
import { getFilesService, getFolderFilesService } from '../../../../../services/AssetService';
import { getObjValueInDeep } from '../../../../../utils/DataStructuresUtils';
import { DateUtil, extractFolderKeys, formatSizeUnits } from '../../../../../utils';
import { motion } from 'framer-motion';
import { FileProps, imageFileComponent } from '../../../../shared/DragAndDrop';
import { ScrollArea } from '../../../../ui/scroll-area';

type Props = {
    skill: AgentSkills;
    setSkillFiles?: (files: (prevState: { [skill_id: number]: FileProps[] }) => any) => void;
    skillFiles?: {
        [skill_id: number]: FileProps[];
    };
};
const AgentSkillDetailModalComponent = ({ skill, setSkillFiles, skillFiles }: Props) => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { data, isLoading } = useQuery({
        queryKey: ['getFolderFilesService', (skill.id ?? '') + extractFolderKeys(skill).length.toString()],
        queryFn: async () => {
            if (skillFiles && skillFiles[skill.id as number] && skillFiles[skill.id as number].length > 0) {
                return skillFiles![skill.id as number];
            }

            let items: FileProps[] = [];
            // const folder = skill.config?.folder

            const folderKeys = extractFolderKeys(skill);

            for await (const folderKey of folderKeys) {
                const response = skill.config.folder
                    ? await getFolderFilesService(folderKey)
                    : await getFilesService(folderKey);
                if (response?.success) {
                    items = skill.config.folder
                        ? items.concat(
                              response.items.map((item) => ({
                                  key: item.key,
                                  name: item.name,
                                  type: item.mimetype,
                                  size: item.size,
                              }))
                          )
                        : [
                              {
                                  key: folderKey,
                                  name: response.item.name,
                                  size: response.item.size,
                                  url: response.item.url,
                                  type: response.item.mime_type,
                              },
                          ];
                }
            }

            return items;
        },
        enabled: extractFolderKeys(skill).length > 0,
    });

    const commonFields: { key: string; label: string; isDate?: boolean }[] = useMemo(
        () => [
            { key: 'skill_id', label: agent('id') },
            { key: 'skill_type', label: agent('skill_type') },
            { key: 'name', label: common('name') },
            { key: 'model', label: common('model') },
            { key: 'description', label: common('description') },
            { key: 'prompt', label: agent('prompt') },
        ],
        []
    );

    const fieldsConfig: Record<SkillType, { key: string; label: string; isDate?: boolean }[]> = useMemo(
        () => ({
            [SkillType.SkillRetriever]: [],
            [SkillType.SkillUnstructuredRetriever]: [
                {
                    key: 'config.vector_store',
                    label: agent('vector_storage'),
                },
                {
                    key: 'config.folder',
                    label: agent('folder'),
                },
                {
                    key: 'config.url',
                    label: agent('url'),
                },
                {
                    key: 'config.vector_store_config.chunk_size',
                    label: common('chunk_size'),
                },
                {
                    key: 'config.vector_store_config.chunk_overlap',
                    label: common('chunk_overlap'),
                },
                {
                    key: 'config.vector_store_config.lambda_mult',
                    label: agent('lambda_mult'),
                },
                {
                    key: 'config.vector_store_config.search_type',
                    label: agent('search_type'), // using defaultValue
                },
                {
                    key: 'config.vector_store_config.fetch_k',
                    label: agent('fetch_k'), // using defaultValue
                },
                {
                    key: 'config.vector_store_config.k',
                    label: agent('k'), // using defaultValue
                },
                {
                    key: 'config.vector_store_config.score_threshold',
                    label: agent('score_threshold'),
                },
            ],
            [SkillType.SkillStructuredRetriever]: [
                {
                    key: 'config.db_host',
                    label: agent('db_host'),
                },
                {
                    key: 'config.db_name',
                    label: agent('db_name'),
                },
                {
                    key: 'config.db_password',
                    label: agent('db_password'),
                },
                {
                    key: 'config.table_name',
                    label: agent('table_name'),
                },
                {
                    key: 'config.columns_to_find',
                    label: agent('columns_to_find'),
                },
                {
                    key: 'config.columns_to_return',
                    label: agent('columns_to_return'),
                },
                {
                    key: 'config.model_name',
                    label: agent('model_name'),
                },
                {
                    key: 'config.fields_to_find',
                    label: agent('fields_to_find'),
                },
                {
                    key: 'config.fields_to_return',
                    label: agent('fields_to_return'),
                },
                {
                    key: 'config.force',
                    label: agent('force'),
                },
            ],
            [SkillType.SkillClassification]: [
                {
                    key: 'config.examples',
                    label: common('examples'),
                },
                {
                    key: 'config.classification_enum',
                    label: agent('classification_enum'),
                },
                {
                    key: 'config.context',
                    label: agent('context'),
                },
            ],
            [SkillType.SkillDataAnalysis]: [
                {
                    key: 'config.db_host',
                    label: agent('db_host'),
                },
                {
                    key: 'config.db_name',
                    label: agent('db_name'),
                },
                {
                    key: 'config.db_password',
                    label: agent('db_password'),
                },
                {
                    key: 'config.context',
                    label: agent('context'),
                },
                {
                    key: 'config.report_key',
                    label: agent('report_key'),
                },
            ],
            [SkillType.SkillRequest]: [
                {
                    key: 'config.url',
                    label: agent('url'),
                },
                {
                    key: 'config.type',
                    label: agent('type'),
                },
                {
                    key: 'config.params',
                    label: agent('params'),
                },
                {
                    key: 'config.headers',
                    label: agent('headers'),
                },
            ],
            [SkillType.SkillSentiment]: [
                {
                    key: 'config.threshold',
                    label: common('threshold'),
                },
            ],
            [SkillType.SkillSummarize]: [
                {
                    key: 'config.folder',
                    label: common('folder'),
                },
                {
                    key: 'config.file',
                    label: common('file'),
                },
                {
                    key: 'config.max_token_output',
                    label: agent('max_token_output'),
                },
                {
                    key: 'config.context',
                    label: agent('context'),
                },
            ],
            [SkillType.SkillImageAnalysis]: [],
            [SkillType.SkillContentGenerator]: [
                {
                    key: 'config.allow_generate_images',
                    label: agent('allow_generate_images'),
                },
            ],
            [SkillType.SkillDocumentAnalysis]: [],
            [SkillType.SkillSpeechToText]: [
                {
                    key: 'config.n_tries',
                    label: agent('n_tries'),
                },
            ],
        }),
        []
    );

    const getListByType = () => {
        if (
            [SkillType.SkillDataAnalysis, SkillType.SkillClassification, SkillType.SkillSummarize].includes(
                skill.skill_type as SkillType
            )
        ) {
            return [
                ...commonFields,
                ...fieldsConfig[skill.skill_type as SkillType],
                ...fieldsConfig[SkillType.SkillRetriever].map((item) => {
                    const key = item.key.split('.').at(-1);
                    return {
                        ...item,
                        key: 'config.action_retriever_config.' + key,
                    };
                }),
            ];
        }

        return [...commonFields, ...fieldsConfig[skill.skill_type as SkillType]];
    };

    React.useEffect(() => {
        if (skill?.id && setSkillFiles && data && data.length > 0) {
            setSkillFiles((prevState) => {
                return {
                    ...prevState,
                    [skill.id as number]: data,
                };
            });
        }
    }, [data, skill, setSkillFiles]);

    const getValueSkillByType = (key: string, value: string | number | string[] | KeyValue[] | boolean) => {
        let nValue = value;
        if (key === 'skill_type') {
            nValue = agent(value as string);
        } else if (
            ['params', 'headers', 'columns_to_find', 'columns_to_return', 'fields_to_find', 'fields_to_return'].some(
                (field) => key.includes(field)
            )
        ) {
            if (
                value &&
                Array.isArray(value) &&
                (value as KeyValue[]).filter((item) => item.name && item.value).length > 0
            ) {
                return (
                    <>
                        {(value as KeyValue[])
                            .filter((item) => item.name && item.value)
                            .map((item, index) => {
                                return (
                                    <p
                                        className="text-base text-gray-800"
                                        key={`${index}_${item.name || ''}_${item.name || ''}`}
                                    >
                                        <span className="font-semibold">{item.name || ''}:</span> {item.value || ''}
                                    </p>
                                );
                            })}
                    </>
                );
            }
        } else {
            if (typeof value === 'boolean') {
                nValue = value ? common('yes') : 'No';
            }

            if (typeof value === 'number') {
                nValue = value;
            }

            if (Array.isArray(value)) {
                nValue = value.join(', ');
            }
        }

        return (
            <p className="text-base font-semibold text-gray-800"> {(nValue as string) || agent('data_no_available')}</p>
        );
    };

    return (
        <ScrollArea className="h-[70vh] p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fieldsConfig[skill.skill_type as SkillType] &&
                    getListByType().map(({ key, label, isDate }, index) => {
                        const value = getObjValueInDeep(skill, key);

                        if (value !== undefined && value !== null) {
                            return (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="bg-white bg-opacity-60 backdrop-blur-sm rounded-xl p-4 shadow-md"
                                >
                                    <h3 className="text-sm font-medium text-purple-600 mb-1">{label}</h3>
                                    {isDate ? (
                                        <p className="text-gray-800">
                                            {new DateUtil(new Date(value), 'yyyy-MM-dd hh:mm:ss a').getDate()}
                                        </p>
                                    ) : (
                                        getValueSkillByType(key, value)
                                    )}
                                </motion.div>
                            );
                        }

                        return null;
                    })}
            </div>

            {isLoading ? (
                <p>
                    {common('loading')} {common('files')}...
                </p>
            ) : null}
            {!isLoading && data && data?.length > 0 ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: getListByType().length * 0.05 }}
                    className="mt-8"
                >
                    <h3 className="text-xl font-semibold text-purple-800 mb-4">{common('files')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {data.map((file, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                className="bg-white bg-opacity-60 backdrop-blur-sm rounded-xl p-4 shadow-md flex items-center space-x-4"
                            >
                                {imageFileComponent(file)}
                                <div>
                                    <p className="font-medium text-gray-800">{file.name}</p>
                                    <p className="text-sm text-gray-500">{formatSizeUnits(file.size)}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            ) : null}
        </ScrollArea>
    );
};

export default AgentSkillDetailModalComponent;
